// src/pages/AdminPage.tsx

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, BookOpen, Settings, Upload, ArrowLeft, Coins, BarChart3, MessageSquare, Radio, FolderSync, AlertTriangle, Zap } from 'lucide-react';
import LogoutButton from '../components/common/LogoutButton';

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <header className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 transition-all active:scale-95"
            >
              <ArrowLeft size={16} />
              返回仪表盘
            </button>
            <h1 className="text-2xl font-bold text-gray-900">管理控制台</h1>
          </div>
          <LogoutButton />
        </header>
      
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* 1. 用户管理 */}
          <Link 
            to="/admin/users" 
            className="block bg-white rounded-xl shadow-sm border border-gray-200 p-8 hover:shadow-md hover:border-brand-300 transition-all text-center group"
          >
            <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <Users size={24} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">用户管理</h3>
            <p className="text-sm text-gray-500">管理系统用户账户</p>
          </Link>

          {/* ===== 新增：Token 管理模块 ===== */}
          <Link 
            to="/admin/token-management" 
            className="block bg-white rounded-xl shadow-sm border border-gray-200 p-8 hover:shadow-md hover:border-yellow-300 transition-all text-center group"
          >
            <div className="w-16 h-16 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <Coins size={24} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Token 财务管理</h3>
            <p className="text-sm text-gray-500">充值、查询余额与消耗报表</p>
          </Link>

          {/* ===== 新增：研发进度跟踪模块 ===== */}
          <Link 
            to="/admin/progress-tracking" 
            className="block bg-white rounded-xl shadow-sm border border-gray-200 p-8 hover:shadow-md hover:border-cyan-300 transition-all text-center group"
          >
            <div className="w-16 h-16 bg-cyan-100 text-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <BarChart3 size={24} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">研发进度跟踪</h3>
            <p className="text-sm text-gray-500">监控课程研发进度与Token消耗</p>
          </Link>

          {/* ===== 新增：教研聊天记录分析模块 ===== */}
          <Link 
            to="/admin/chat-records-analysis" 
            className="block bg-white rounded-xl shadow-sm border border-gray-200 p-8 hover:shadow-md hover:border-pink-300 transition-all text-center group"
          >
            <div className="w-16 h-16 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <MessageSquare size={24} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">教研聊天记录分析</h3>
            <p className="text-sm text-gray-500">分析用户AI交互记录与教研活动</p>
          </Link>

          {/* ===== 新增：用量预警查询模块 ===== */}
          <Link 
            to="/admin/usage-warning" 
            className="block bg-white rounded-xl shadow-sm border border-gray-200 p-8 hover:shadow-md hover:border-orange-300 transition-all text-center group"
          >
            <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <AlertTriangle size={24} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">用量预警查询</h3>
            <p className="text-sm text-gray-500">查询教案和课件开发过程中的风险聊天记录</p>
          </Link>
          
          {/* 2. 用户课程权限 */}
          <Link 
            to="/admin/user-lessons" 
            className="block bg-white rounded-xl shadow-sm border border-gray-200 p-8 hover:shadow-md hover:border-green-300 transition-all text-center group"
          >
            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <BookOpen size={24} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">课程权限</h3>
            <p className="text-sm text-gray-500">管理用户的课程访问权限</p>
          </Link>
          
          {/* 3. 课程发布 */}
          <Link 
            to="/admin/lesson-public" 
            className="block bg-white rounded-xl shadow-sm border border-gray-200 p-8 hover:shadow-md hover:border-orange-300 transition-all text-center group"
          >
            <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <Upload size={24} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">课程发布</h3>
            <p className="text-sm text-gray-500">发布和管理课程内容</p>
          </Link>
          
          {/* 4. 系统设置 */}
          <Link 
            to="/settings" 
            className="block bg-white rounded-xl shadow-sm border border-gray-200 p-8 hover:shadow-md hover:border-purple-300 transition-all text-center group"
          >
            <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <Settings size={24} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">系统设置</h3>
            <p className="text-sm text-gray-500">管理AI提示词与配置</p>
          </Link>

          {/* ===== 新增：AI 接口测试与监控模块 ===== */}
          <Link 
            to="/admin/ai-interface-test" 
            className="block bg-white rounded-xl shadow-sm border border-gray-200 p-8 hover:shadow-md hover:border-red-300 transition-all text-center group"
          >
            <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <Radio size={24} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">AI 接口监控</h3>
            <p className="text-sm text-gray-500">测试AI接口状态与切换模型</p>
          </Link>

          {/* ===== 新增：Vibe Coding 重新归类模块 ===== */}
          <Link 
            to="/admin/vibe-coding-reclassify" 
            className="block bg-white rounded-xl shadow-sm border border-gray-200 p-8 hover:shadow-md hover:border-indigo-300 transition-all text-center group"
          >
            <div className="w-16 h-16 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <FolderSync size={24} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Vibe Coding 重新归类</h3>
            <p className="text-sm text-gray-500">AI重新归类、分类管理与手动修改</p>
          </Link>

          {/* ===== 新增：批量生成教案模块 ===== */}
          <Link 
            to="/admin/batch-plan-generation" 
            className="block bg-white rounded-xl shadow-sm border border-gray-200 p-8 hover:shadow-md hover:border-purple-300 transition-all text-center group"
          >
            <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <Zap size={24} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">批量生成教案</h3>
            <p className="text-sm text-gray-500">多Agent一键生成多个课时教案</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
